import { useEffect, useMemo, useState } from "react";
import cogoToast from "cogo-toast";
import { Button, Form, Nav, Table } from "react-bootstrap";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import {
    useGetCorporateApplicationsQuery,
    useGetPendingBusinessKybAttemptsQuery,
    useRetryPendingBusinessKybAttemptsMutation,
} from "../../../../redux/apiSlice";
import { formatDate, formatStatus } from "../../../util/Helpers";
import { ViewIcon } from "../../../util/Icons";
import CorporateApplicationModal from "./modal/CorporateApplicationModal";
import CreateCorporateApplicationWizard from "./wizard/CreateCorporateApplicationWizard";
import { canEditReps } from "./corporateEditRules";

const CORPORATE_ACCOUNT_PAGE_TABS = [
    { key: "all-accounts", label: "All Corporate Accounts" },
    { key: "pending-accounts", label: "Pending Corporate Accounts" },
    { key: "created-accounts", label: "Created Corporate Accounts" },
    { key: "pending-kyb-attempts", label: "Pending KYB Attempts" },
    { key: "rejected-accounts", label: "Rejected Corporate Accounts" },
];

const prettyEnum = (value) => `${value ?? ""}`.replace(/_/g, " ").trim();

const asText = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number") return `${value}`;
    if (typeof value === "string") return value.trim() || "-";
    return "-";
};

const normalizeList = (value) => {
    if (Array.isArray(value)) return value;

    const candidates = [
        value?.data,
        value?.items,
        value?.results,
        value?.rows,
        value?.data?.data,
        value?.data?.items,
        value?.data?.results,
        value?.data?.rows,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
    }

    return [];
};

const normalizeAttemptState = (attempt, prefix) => ({
    triesLeft: attempt?.[`${prefix}TriesLeft`] ?? null,
    maxTries: attempt?.[`${prefix}MaxTries`] ?? null,
    daysLeft: attempt?.[`${prefix}DaysLeft`] ?? null,
    pending: attempt?.[`${prefix}Pending`] ?? false,
    pendingInput: attempt?.[`${prefix}PendingInput`] ?? null,
    suspended: attempt?.[`${prefix}Suspended`] ?? false,
    suspendedUntil: attempt?.[`${prefix}SuspendedUntil`] ?? null,
});

const renderApplicationStatus = (value) => {
    const normalized = `${value ?? ""}`.trim().toUpperCase();

    if (!normalized) return "-";

    const color = {
        DRAFT: "secondary",
        IN_REVIEW: "warning",
        APPROVED: "success",
        REJECTED: "danger",
        ACCOUNT_CREATED: "primary",
    }[normalized] || "secondary";

    return (
        <span className={`label label-lg label-light-${color} label-inline`}>
            {normalized === "DRAFT" ? "Pending" : prettyEnum(normalized)}
        </span>
    );
};

const renderActionLabel = (value) => {
    if (!value) return "None";

    return (
        <span
            className="label label-lg label-light-success label-inline"
            style={{ whiteSpace: "nowrap" }}
        >
            {prettyEnum(value)}
        </span>
    );
};

const renderAttemptState = (attempt) => {
    if (attempt?.pending) return renderActionLabel("pending");
    if (attempt?.suspended) return (
        <span className="label label-lg label-light-danger label-inline">
            Suspended
        </span>
    );

    return (
        <span className="label label-lg label-light-primary label-inline">
            Available
        </span>
    );
};

// A draft is an application started in the wizard but not yet submitted for review.
const isDraftApplication = (application) =>
    `${application?.applicationStatus ?? application?.status ?? ""}`.trim().toUpperCase() === "DRAFT";

const ApplicationsTable = ({
    applications,
    isFetching,
    emptyMessage,
    onViewApplication,
    onContinueApplication,
}) => (
    <div className="table-responsive">
        <Table className="table table-head-custom table-vertical-center overflow-hidden">
            <thead>
                <tr>
                    <th>Account Name</th>
                    <th>Entity Name</th>
                    <th>Registration No.</th>
                    <th>Status</th>
                    <th>KYC Status</th>
                    <th>Next Admin Action</th>
                    <th>Updated</th>
                    <th className="text-right">Action</th>
                </tr>
            </thead>
            <tbody>
                {applications.map((application) => (
                    <tr key={application?.applicationId}>
                        <td>
                            <div className="font-weight-bolder text-dark">
                                {asText(application?.accountName)}
                            </div>
                            <div className="text-muted font-size-sm">
                                {asText(application?.applicationId)}
                            </div>
                        </td>
                        <td>{asText(application?.entityName)}</td>
                        <td>{asText(application?.companyRegistrationNumber)}</td>
                        <td>{renderApplicationStatus(application?.applicationStatus)}</td>
                        <td>{formatStatus(application?.kycStatus) || asText(application?.kycStatus)}</td>
                        <td>{renderActionLabel(application?.nextAdminAction)}</td>
                        <td>{formatDate(application?.updated || application?.created)}</td>
                        <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                            {onContinueApplication && !isDraftApplication(application) && canEditReps(application) && (
                                <Button
                                    title="Edit company details or representatives"
                                    variant="light-primary"
                                    size="sm"
                                    className="mr-2 font-weight-bold"
                                    onClick={() => onContinueApplication(application)}
                                >
                                    Edit
                                </Button>
                            )}
                            {onContinueApplication && isDraftApplication(application) && (
                                <Button
                                    title="Continue filling in this draft"
                                    variant="light-primary"
                                    size="sm"
                                    className="mr-2 font-weight-bold"
                                    onClick={() => onContinueApplication(application)}
                                >
                                    Continue
                                </Button>
                            )}
                            <Button
                                title="View Application"
                                className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                                onClick={() => onViewApplication(application)}
                            >
                                <span className="svg-icon svg-icon-md svg-icon-primary">
                                    <ViewIcon />
                                </span>
                            </Button>
                        </td>
                    </tr>
                ))}

                {!isFetching && applications.length === 0 && (
                    <tr>
                        <td colSpan="8" className="text-center text-muted py-10">
                            {emptyMessage}
                        </td>
                    </tr>
                )}

                {isFetching && applications.length === 0 && (
                    <tr>
                        <td colSpan="8" className="text-center text-muted py-10">
                            Loading corporate applications...
                        </td>
                    </tr>
                )}
            </tbody>
        </Table>
    </div>
);

export const CorporateAccountsManagement = ({
    match: {
        params: { userGroup },
    },
}) => {
    const [activePageTab, setActivePageTab] = useState("all-accounts");
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [showCreateWizard, setShowCreateWizard] = useState(false);
    // Draft picked with "Continue": the wizard reopens on it instead of starting fresh.
    const [resumeApplication, setResumeApplication] = useState(null);
    const continueApplication = (application) => {
        setResumeApplication(application);
        setShowCreateWizard(true);
    };
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");

    useEffect(() => {
        const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(handle);
    }, [searchInput]);

    const {
        currentData: applicationsResponse,
        isFetching,
    } = useGetCorporateApplicationsQuery(
        {
            query: {
                userGroup,
                search,
            },
        },
        {
            skip: activePageTab !== "all-accounts",
            refetchOnMountOrArgChange: true,
        }
    );
    const {
        currentData: allApplicationsResponse,
        isFetching: isFetchingAllApplications,
    } = useGetCorporateApplicationsQuery(
        {
            query: {
                userGroup,
                search,
            },
        },
        {
            skip: activePageTab !== "pending-accounts" && activePageTab !== "rejected-accounts",
            refetchOnMountOrArgChange: true,
        }
    );
    const {
        currentData: pendingBusinessKybAttemptsResponse,
        isFetching: isFetchingPendingBusinessKybAttempts,
    } = useGetPendingBusinessKybAttemptsQuery(
        {
            query: {
                limit: 0,
                offset: 0,
            },
        },
        {
            skip: activePageTab !== "pending-kyb-attempts",
        }
    );
    const [
        retryPendingBusinessKybAttempts,
        {
            isLoading: isRetryingPendingBusinessKybAttempts,
            isSuccess: retriedPendingBusinessKybAttemptsSuccessfully,
        },
    ] = useRetryPendingBusinessKybAttemptsMutation();

    const applications = useMemo(
        () => normalizeList(applicationsResponse),
        [applicationsResponse]
    );
    const allApplications = useMemo(
        () => normalizeList(allApplicationsResponse),
        [allApplicationsResponse]
    );
    const pendingCorporateAccounts = useMemo(
        () => allApplications.filter((application) => {
            const normalizedStatus = `${application?.applicationStatus ?? application?.status ?? ""}`.trim().toUpperCase();
            return normalizedStatus === "IN_REVIEW" || normalizedStatus === "DRAFT";
        }),
        [allApplications]
    );
    const rejectedCorporateAccounts = useMemo(
        () => allApplications.filter((application) => {
            const normalizedStatus = `${application?.applicationStatus ?? application?.status ?? ""}`.trim().toUpperCase();
            return normalizedStatus === "REJECTED";
        }),
        [allApplications]
    );
    const {
        currentData: createdAccountsResponse,
        isFetching: isFetchingCreatedAccounts,
    } = useGetCorporateApplicationsQuery(
        {
            query: {
                userGroup,
                search,
                status: "ACCOUNT_CREATED",
            },
        },
        {
            skip: activePageTab !== "created-accounts",
        }
    );
    const createdAccounts = useMemo(
        () => normalizeList(createdAccountsResponse),
        [createdAccountsResponse]
    );
    const pendingBusinessKybAttempts = useMemo(
        () => normalizeList(pendingBusinessKybAttemptsResponse)
            .filter((attempt) => `${attempt?.userGroup ?? ""}`.toLowerCase() === `${userGroup ?? ""}`.toLowerCase()),
        [pendingBusinessKybAttemptsResponse, userGroup]
    );

    useEffect(() => {
        if (retriedPendingBusinessKybAttemptsSuccessfully) {
            cogoToast.success("Pending business KYB attempts retried");
        }
    }, [retriedPendingBusinessKybAttemptsSuccessfully]);

    return (
        <>
            <div className="py-4 px-6">
                <Card>
                    <CardHeader title="Corporate Accounts">
                        <CardHeaderToolbar>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setResumeApplication(null);
                                    setShowCreateWizard(true);
                                }}
                            >
                                + Create Application
                            </Button>
                        </CardHeaderToolbar>
                    </CardHeader>
                    <CardBody>
                        <Nav variant="tabs" className="mb-5">
                            {CORPORATE_ACCOUNT_PAGE_TABS.map(({ key, label }) => (
                                <Nav.Item key={key}>
                                    <Nav.Link
                                        active={activePageTab === key}
                                        onClick={() => setActivePageTab(key)}
                                        className="cursor-pointer"
                                        style={{ cursor: "pointer" }}
                                    >
                                        {label}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>

                        {activePageTab !== "pending-kyb-attempts" && (
                            <div className="mb-5 position-relative" style={{ maxWidth: 420 }}>
                                <Form.Control
                                    type="text"
                                    placeholder="Search corporate applications..."
                                    value={searchInput}
                                    onChange={(event) => setSearchInput(event.target.value)}
                                    style={{ paddingRight: searchInput ? 32 : undefined }}
                                />
                                {!!searchInput && (
                                    <span
                                        role="button"
                                        aria-label="Clear search"
                                        onClick={() => {
                                            setSearchInput("");
                                            setSearch("");
                                        }}
                                        className="text-muted"
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            cursor: "pointer",
                                            fontSize: 18,
                                            lineHeight: 1,
                                        }}
                                    >
                                        &times;
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-5">
                            <div className="text-muted font-size-sm">
                                {activePageTab === "all-accounts"
                                    ? `Showing all corporate applications for ${userGroup}.`
                                    : activePageTab === "pending-accounts"
                                    ? `Showing pending corporate accounts for ${userGroup}.`
                                    : activePageTab === "created-accounts"
                                    ? `Showing created corporate accounts for ${userGroup}.`
                                    : activePageTab === "rejected-accounts"
                                    ? `Showing rejected corporate accounts for ${userGroup}.`
                                    : `Showing pending KYB provider attempts for ${userGroup}.`}
                            </div>
                            <div className="text-muted font-size-sm">
                                {activePageTab === "all-accounts"
                                    ? (isFetching ? "Refreshing..." : `${applications.length} application${applications.length === 1 ? "" : "s"}`)
                                    : activePageTab === "pending-accounts"
                                    ? (isFetchingAllApplications ? "Refreshing..." : `${pendingCorporateAccounts.length} application${pendingCorporateAccounts.length === 1 ? "" : "s"}`)
                                    : activePageTab === "created-accounts"
                                    ? (isFetchingCreatedAccounts ? "Refreshing..." : `${createdAccounts.length} application${createdAccounts.length === 1 ? "" : "s"}`)
                                    : activePageTab === "rejected-accounts"
                                    ? (isFetchingAllApplications ? "Refreshing..." : `${rejectedCorporateAccounts.length} application${rejectedCorporateAccounts.length === 1 ? "" : "s"}`)
                                    : (isFetchingPendingBusinessKybAttempts ? "Refreshing..." : `${pendingBusinessKybAttempts.length} attempt${pendingBusinessKybAttempts.length === 1 ? "" : "s"}`)}
                            </div>
                        </div>

                        {activePageTab === "all-accounts" && (
                            <ApplicationsTable
                                applications={applications}
                                isFetching={isFetching}
                                emptyMessage={search ? `No corporate applications match "${search}".` : "No corporate applications found yet."}
                                onViewApplication={setSelectedApplication}
                                onContinueApplication={continueApplication}
                            />
                        )}

                        {activePageTab === "pending-accounts" && (
                            <ApplicationsTable
                                applications={pendingCorporateAccounts}
                                isFetching={isFetchingAllApplications}
                                emptyMessage="No pending corporate accounts found yet."
                                onViewApplication={setSelectedApplication}
                                onContinueApplication={continueApplication}
                            />
                        )}

                        {activePageTab === "created-accounts" && (
                            <ApplicationsTable
                                applications={createdAccounts}
                                isFetching={isFetchingCreatedAccounts}
                                emptyMessage="No created corporate accounts found yet."
                                onViewApplication={setSelectedApplication}
                                onContinueApplication={continueApplication}
                            />
                        )}

                        {activePageTab === "rejected-accounts" && (
                            <ApplicationsTable
                                applications={rejectedCorporateAccounts}
                                isFetching={isFetchingAllApplications}
                                emptyMessage="No rejected corporate accounts found yet."
                                onViewApplication={setSelectedApplication}
                                onContinueApplication={continueApplication}
                            />
                        )}

                        {activePageTab === "pending-kyb-attempts" && (
                            <>
                                <div className="d-flex flex-wrap justify-content-between align-items-center mb-5">
                                    <div className="text-muted font-size-sm">
                                        This view lists stuck CAC / TIN provider attempts that can be retried in one action.
                                    </div>
                                    <Button
                                        variant="success"
                                        disabled={isRetryingPendingBusinessKybAttempts || pendingBusinessKybAttempts.length === 0}
                                        onClick={() => retryPendingBusinessKybAttempts()}
                                        style={{
                                            backgroundColor: "#22A455",
                                            borderColor: "#22A455",
                                        }}
                                    >
                                        {isRetryingPendingBusinessKybAttempts ? "Retrying..." : "Retry Stuck Attempts"}
                                    </Button>
                                </div>

                                <div className="table-responsive">
                                    <Table className="table table-head-custom table-vertical-center overflow-hidden">
                                        <thead>
                                            <tr>
                                                <th>Application</th>
                                                <th>Registration No.</th>
                                                <th>CAC Attempts</th>
                                                <th>CAC State</th>
                                                <th>TIN Attempts</th>
                                                <th>TIN State</th>
                                                <th>Updated</th>
                                                <th className="text-right">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingBusinessKybAttempts.map((attempt) => {
                                                const cacAttempt = normalizeAttemptState(attempt, "cac");
                                                const tinAttempt = normalizeAttemptState(attempt, "tin");

                                                return (
                                                    <tr key={attempt?.attemptId || attempt?.applicationId}>
                                                        <td>
                                                            <div className="font-weight-bolder text-dark">
                                                                {asText(attempt?.applicationId)}
                                                            </div>
                                                            <div className="text-muted font-size-sm">
                                                                {asText(attempt?.userId)}
                                                            </div>
                                                        </td>
                                                        <td>{asText(cacAttempt?.pendingInput?.companyRegistrationNumber)}</td>
                                                        <td>{`${asText(cacAttempt?.triesLeft)} / ${asText(cacAttempt?.maxTries)}`}</td>
                                                        <td>{renderAttemptState(cacAttempt)}</td>
                                                        <td>{`${asText(tinAttempt?.triesLeft)} / ${asText(tinAttempt?.maxTries)}`}</td>
                                                        <td>{renderAttemptState(tinAttempt)}</td>
                                                        <td>{formatDate(attempt?.updated || attempt?.created)}</td>
                                                        <td className="text-right">
                                                            <Button
                                                                title="View Application"
                                                                className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                                                                onClick={() => setSelectedApplication({
                                                                    applicationId: attempt?.applicationId,
                                                                })}
                                                            >
                                                                <span className="svg-icon svg-icon-md svg-icon-primary">
                                                                    <ViewIcon />
                                                                </span>
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}

                                            {!isFetchingPendingBusinessKybAttempts && pendingBusinessKybAttempts.length === 0 && (
                                                <tr>
                                                    <td colSpan="8" className="text-center text-muted py-10">
                                                        No pending business KYB attempts found for this user group.
                                                    </td>
                                                </tr>
                                            )}

                                            {isFetchingPendingBusinessKybAttempts && pendingBusinessKybAttempts.length === 0 && (
                                                <tr>
                                                    <td colSpan="8" className="text-center text-muted py-10">
                                                        Loading pending business KYB attempts...
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </CardBody>
                </Card>
            </div>

            <CorporateApplicationModal
                show={!!selectedApplication}
                applicationId={selectedApplication?.applicationId}
                summary={selectedApplication}
                onHide={() => setSelectedApplication(null)}
            />

            <CreateCorporateApplicationWizard
                show={showCreateWizard}
                userGroup={userGroup}
                resumeApplicationId={resumeApplication?.applicationId}
                onHide={() => {
                    setShowCreateWizard(false);
                    setResumeApplication(null);
                }}
            />
        </>
    );
};
